import React, {useState, useEffect, useCallback} from "react";
import {
    Grid,
    Box,
    Typography,
    Container,
    Button,
    Modal,
    Fade,
    Theme,
    Backdrop,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Avatar,
} from "@material-ui/core";
import {makeStyles} from "@material-ui/core/styles";
import {useSnackbar} from "notistack";
import AvaliableVetListItem from "../../Components/ListItems/AvaliableVet";
import API from "../../helpers/apiHelpers";
import moment from "moment";
import {specialities} from "../../Constants/specialities";
import {states} from "../../Constants/locations";
import {colorWhite} from "../../Constants/theme";
import Calendar from "../../Components/Calendar";
import {useHistory} from "react-router-dom";
import {useSelector} from "react-redux";
import {RootState, useAppDispatch} from "../../redux/store";
import {
    updateServiceType,
    updateRequestReason,
    updateVetId,
    updateVetProfile,
    updateVetProfileId,
    updatePickedDate,
    updatePickedTimeSlot,
    updateSelectedPetID,
    updateIsImmediate,
    updateSelectedState,
} from "../../redux/scheduleAppointment/scheduling.actions";
import {setPrice} from "../../redux/scheduleAppointment/scheduling.reducers";
import {
    clearGuestBooking,
    getGuestBooking,
    GuestBookingState,
    setGuestBooking,
    updateGuestBooking,
    DEFAULT_GUEST_SERVICE_TYPE,
    DEFAULT_GUEST_REASON_TYPE,
    fetchAndApplyAppointmentDraft,
    applyGuestBookingToSchedule,
    getGuestBookingPrice,
    ensureSchedulePrice,
} from "../../utils/guestBooking";
import {petMatchesGuestType} from "../../utils/guestPetMatching";
import {setPetType} from "../../utils/petInfo";
import {
    getOtherPetTypes,
    isBasicPetType,
    PetTypeOption,
} from "../../utils/petTypeOptions";
import Image from "material-ui-image";
import {BirdIconSVG, ChickenIconSVG, PetCatSVG, PetDogSVG} from "../../utils/petTypeIcons";
import {ReactComponent as LogoSVG} from "../../Images/logo.svg";

const useStyle = makeStyles((theme: Theme) => ({
    wrapper: {
        height: "100%",
        marginTop: 90,
        marginBottom: 50,
        alignItems: "flex-start",
    },
    modal: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        [theme.breakpoints.down("sm")]: {
            width: "99%",
            margin: "0 auto",
        },
    },
    paper: {
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[5],
        padding: theme.spacing(3, 3),
        display: "flex",
        flexDirection: "column",
        maxWidth: 480,
        width: "100%",
    },
    button: {
        width: "100%",
        marginTop: 12,
    },
    section: {
        width: "100%",
        marginBottom: 32,
    },
    sectionTitle: {
        marginBottom: 16,
        textAlign: "left",
    },
    leftAligned: {
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
    },
    selectWidth: {
        minWidth: 220,
    },
    avatar: {
        width: 95,
        height: 95,
        cursor: "pointer",
        backgroundColor: colorWhite,
    },
    selectedPet: {
        outline: "3px solid #65CCCE",
    },
}));

type FlowStep = "availability" | "pet_select";

const DEFAULT_SERVICE_TYPE = DEFAULT_GUEST_SERVICE_TYPE;
const DEFAULT_REASON_TYPE = DEFAULT_GUEST_REASON_TYPE;
const OTHER_PET_TYPE_VALUE = "OTHER";

const specialityOptions: PetTypeOption[] = specialities.map(({label, value}) => ({label, value}));
const otherSpecialityOptions = getOtherPetTypes(specialityOptions);

const getBasicPetTypeValue = (petType: string, isOtherTypeSelected: boolean) => {
    if (petType === "CANINE") {
        return "CANINE";
    }
    if (petType === "FELINE") {
        return "FELINE";
    }
    if (isOtherTypeSelected || (petType && !isBasicPetType(petType))) {
        return OTHER_PET_TYPE_VALUE;
    }
    return "";
};

const getTimeslotsForSelectedDate = (scheduling, dateTime) => {
    const dateTimeSlots = scheduling.find((item) => moment(item.date).isSame(dateTime, "day"));
    return (dateTimeSlots || {timeslots: []}).timeslots;
};

const getSchedulingForUserTimezone = (schedule) => {
    const newSchedule = schedule
        .reduce((acc, item) => [...item.timeslots, ...acc], [])
        .sort((a, b) => moment(a).diff(moment(b)))
        .map((item) => moment.tz(item, moment.tz.guess()))
        .filter((timeslot) => timeslot > moment())
        .reduce((acc, item) => {
            const key = item.format("YYYY-MM-DD");
            if (!acc[key]) {
                acc[key] = {date: key, timeslots: []};
            }
            acc[key].timeslots.push(item.format());
            return acc;
        }, {});
    return Object.values(newSchedule);
};

const GuestAvailableVet = () => {
    const classes = useStyle();
    const dispatch = useAppDispatch();
    const history = useHistory();
    const {enqueueSnackbar} = useSnackbar();
    const [step, setStep] = useState<FlowStep>("availability");
    const [selectedPetType, setSelectedPetType] = useState("");
    const [selectedState, setSelectedState] = useState("");
    const [specialtyFilter, setSpecialtyFilter] = useState("");
    const [isOtherTypeSelected, setIsOtherTypeSelected] = useState(false);
    const [vetProfiles, setVetProfiles] = useState([]);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
    const [showContactUs, setShowContactUs] = useState(false);
    const [showSpinner, setShowSpinner] = useState(false);
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [petsList, setPetsList] = useState([]);
    const [selectedPetId, setSelectedPetId] = useState<number | null>(null);

    const timeZone = moment.tz.guess();
    const location = useSelector((state: RootState) => state?.common.location);
    const isWhiteLabel = useSelector((state: RootState) => state?.branding.partnerPreferencesOn);
    const branding = useSelector((state: RootState) => state?.branding);
    const user = useSelector((state: RootState) => state?.user);
    const selectedPetName = (() => {
        const pet = petsList.find(pet => pet.id === selectedPetId);
        return pet?.name
            ? pet.name
                .trim()
                .toLowerCase()
                .replace(/\b\w/g, character => character.toUpperCase())
            : "your pet";
    })();

    const buildVetParams = useCallback(
        (day: Date, petType: string, specialty: string) => {
            const date = moment(day).tz(timeZone, true);
            const specialtyList = specialty ? [specialty] : petType ? [petType] : [];
            return {
                workingStates: location || selectedState,
                specialties: specialtyList,
                clientTimeZone: timeZone,
                rangeBegins: date.format("YYYY-MM-DD"),
                rangeEnds: date.clone().add(1, "days").format("YYYY-MM-DD"),
            };
        },
        [timeZone, location, selectedState]
    );

    const fetchVets = async (day: Date, petType: string, specialty: string) => {
        if (!petType) return;
        setShowSpinner(true);
        setSelectedDate(day);
        try {
            const params = buildVetParams(day, petType, specialty || petType);
            const profiles = await API.getPublicVetProfiles(params);
            setVetProfiles(profiles || []);
        } catch (err) {
            console.error(err);
            setVetProfiles([]);
        } finally {
            setShowSpinner(false);
        }
    };

    const loadEligiblePets = useCallback(
        async (petType: string) => {
            if (!user?.userId) return;
            try {
                const response = await API.getAnimal(user.userId);
                const eligible = (response || []).filter((pet) => petMatchesGuestType(pet, petType));
                setPetsList(eligible);
                setSelectedPetId(eligible.length === 1 ? eligible[0].id : null);
            } catch (err) {
                console.error(err);
                enqueueSnackbar("Could not load your pets. Please try again.", {variant: "error"});
            }
        },
        [user?.userId, enqueueSnackbar]
    );

    const restoreGuestSession = useCallback(async () => {
        let saved = getGuestBooking();
        if (!saved?.petType && API.isLoggedIn && user?.userId) {
            saved = await fetchAndApplyAppointmentDraft();
        }
        if (!saved?.petType) return;

        setSelectedPetType(saved.petType);
        setSpecialtyFilter(saved.specialtyFilter || saved.petType);
        setSelectedState(saved.state);
        setIsOtherTypeSelected(!isBasicPetType(saved.petType));

        if (saved.vetProfileId && saved.pickedTimeSlot?.length) {
            let vetProfile = saved.vetProfile;
            if (!(vetProfile as {id?: number})?.id) {
                try {
                    const day = saved.pickedDate ? new Date(saved.pickedDate) : new Date();
                    const params = buildVetParams(day, saved.petType, saved.specialtyFilter || saved.petType);
                    const profiles = await API.getPublicVetProfiles(params);
                    const matched = (profiles || []).find((p) => p.id === saved.vetProfileId);
                    if (matched) {
                        vetProfile = matched;
                        updateGuestBooking({vetProfile: matched});
                    }
                } catch (err) {
                    console.error(err);
                }
            }
            applyGuestBookingToSchedule(dispatch, {...saved, vetProfile});
        } else {
            dispatch(updateServiceType(saved.serviceType || DEFAULT_SERVICE_TYPE));
            dispatch(updateRequestReason(saved.reasonType || DEFAULT_GUEST_REASON_TYPE));
            dispatch(updateIsImmediate(false));
        }

        if (API.isLoggedIn && user?.userId && (saved.step === "pet_select" || saved.vetProfileId)) {
            setStep("pet_select");
            await loadEligiblePets(saved.petType);
        } else if (saved.petType) {
            setStep("availability");
            await fetchVets(
                saved.pickedDate ? new Date(saved.pickedDate) : new Date(),
                saved.petType,
                saved.specialtyFilter || saved.petType
            );
        }
    }, [user?.userId, dispatch, loadEligiblePets, buildVetParams]);

    useEffect(() => {
        restoreGuestSession();
    }, [restoreGuestSession]);

    useEffect(() => {
        if (!vetProfiles.length) {
            setAvailableTimeSlots([]);
            return;
        }
        const newTimeSlots = [];
        vetProfiles.forEach((profile) => {
            const schedulingForUserTimezone = getSchedulingForUserTimezone(profile.schedulingDays);
            const timeslots = getTimeslotsForSelectedDate(schedulingForUserTimezone, selectedDate);
            newTimeSlots.push(...timeslots);
        });
        setAvailableTimeSlots(newTimeSlots);
    }, [vetProfiles, selectedDate, selectedState]);

    const applyPetTypeSelection = (value: string) => {
        setSpecialtyFilter(value);
        setSelectedPetType(value);
        const existing = getGuestBooking();
        setGuestBooking({
            petType: value,
            specialtyFilter: value,
            vetProfileId: existing?.vetProfileId || 0,
            vetId: existing?.vetId || 0,
            vetProfile: existing?.vetProfile || {},
            pickedDate: existing?.pickedDate || "",
            pickedTimeSlot: existing?.pickedTimeSlot || [],
            serviceType: DEFAULT_SERVICE_TYPE,
            state: existing?.state || selectedState,
            reasonType: DEFAULT_REASON_TYPE,
            step: existing?.step,
        });
        if (selectedState) {
            fetchVets(selectedDate, value, value);
        }
    };

    const handleBasicPetTypeChange = (value: string) => {
        if (value === OTHER_PET_TYPE_VALUE) {
            setIsOtherTypeSelected(true);
            setSelectedPetType("");
            setSpecialtyFilter("");
            setVetProfiles([]);
            return;
        }

        setIsOtherTypeSelected(false);
        applyPetTypeSelection(value);
    };

    const handleOtherPetTypeChange = (value: string) => {
        applyPetTypeSelection(value);
    };

    const handleStateChange = (value: string) => {
        setSelectedState(value);
        const existing = getGuestBooking();
        const petType = existing?.petType || specialtyFilter || selectedPetType;
        setGuestBooking({
            petType: existing?.petType || specialtyFilter,
            specialtyFilter: existing?.petType || specialtyFilter,
            vetProfileId: existing?.vetProfileId || 0,
            vetId: existing?.vetId || 0,
            vetProfile: existing?.vetProfile || {},
            pickedDate: existing?.pickedDate || "",
            pickedTimeSlot: existing?.pickedTimeSlot || [],
            serviceType: DEFAULT_SERVICE_TYPE,
            state: value,
            reasonType: DEFAULT_REASON_TYPE,
            step: existing?.step,
        });
        if (petType) {
            fetchVets(selectedDate, petType, existing?.specialtyFilter || specialtyFilter || petType);
        }
    };

    const handleTimeSlotSelect = (
        profile: object,
        profileId: number,
        userId: number,
        timeslot: string
    ) => {
        const booking: GuestBookingState = {
            petType: selectedPetType,
            specialtyFilter: specialtyFilter || selectedPetType,
            vetProfileId: profileId,
            vetId: userId,
            vetProfile: profile,
            pickedDate: moment(timeslot).toISOString(),
            pickedTimeSlot: [moment(timeslot).format()],
            serviceType: DEFAULT_SERVICE_TYPE,
            state: selectedState,
            reasonType: DEFAULT_REASON_TYPE,
            step: "pet_select",
        };
        setGuestBooking(booking);
        dispatch(updateServiceType(DEFAULT_SERVICE_TYPE));
        dispatch(updateRequestReason(DEFAULT_REASON_TYPE));
        dispatch(updateIsImmediate(false));
        dispatch(setPrice(getGuestBookingPrice(DEFAULT_SERVICE_TYPE)));
        dispatch(updateVetProfileId(profileId));
        dispatch(updateVetProfile(profile));
        dispatch(updateVetId(userId));
        dispatch(updateSelectedState(selectedState));
        dispatch(updatePickedDate(moment(timeslot).toISOString()));
        dispatch(updatePickedTimeSlot([moment(timeslot).format()]));

        if (API.isLoggedIn && user?.userId) {
            setStep("pet_select");
            loadEligiblePets(selectedPetType);
        } else {
            setShowAuthModal(true);
        }
    };

    const handleLogin = () => {
        setShowAuthModal(false);
        history.push({
            pathname: "/login",
            search: "?login=true",
            state: {returnTo: "/guest/book"},
        });
    };

    const handleSignUp = () => {
        setShowAuthModal(false);
        history.push({
            pathname: "/signUp",
            state: {returnTo: "/guest/book"},
        });
    };

    const continueToAppointmentFlow = () => {
        const booking = getGuestBooking();
        if (booking) {
            applyGuestBookingToSchedule(dispatch, booking);
        } else {
            ensureSchedulePrice(dispatch, DEFAULT_GUEST_SERVICE_TYPE);
        }
        clearGuestBooking();
        history.push("/summary");
    };

    const handlePetContinue = () => {
        if (!selectedPetId) {
            enqueueSnackbar("Please select a pet", {variant: "error"});
            return;
        }
        dispatch(updateSelectedPetID(selectedPetId));
        updateGuestBooking({step: "complete"});
        continueToAppointmentFlow();
    };

    const handleAddPet = () => {
        history.push({
            pathname: "/pets/0/general",
            state: {
                returnTo: "/guest/book",
                guestPetType: selectedPetType,
            },
        });
    };

    const renderPetAvatar = (pet) => {
        if (isWhiteLabel) {
            return (
                <Image
                    alt="pet"
                    src={pet.imageUrl}
                    errorIcon={<img alt="" src={branding.logo} width="100%" />}
                    imageStyle={{objectFit: "cover"}}
                    style={{width: "100%"}}
                />
            );
        }
        const typeKey = setPetType(pet.type);
        if (typeKey === "FELINE") {
            return (
                <Image alt="cat" src={pet.imageUrl} errorIcon={<PetCatSVG />} imageStyle={{objectFit: "cover"}} style={{width: "100%"}} />
            );
        }
        if (typeKey === "CANINE") {
            return (
                <Image alt="dog" src={pet.imageUrl} errorIcon={<PetDogSVG />} imageStyle={{objectFit: "cover"}} style={{width: "100%"}} />
            );
        }
        if (typeKey === "AVIAN") {
            return (
                <Image alt="bird" src={pet.imageUrl} errorIcon={<BirdIconSVG />} imageStyle={{objectFit: "cover"}} style={{width: "100%"}} />
            );
        }
        if (typeKey === "CHICKEN") {
            return (
                <Image alt="chicken" src={pet.imageUrl} errorIcon={<ChickenIconSVG />} imageStyle={{objectFit: "cover"}} style={{width: "100%"}} />
            );
        }
        return pet.imageUrl ? (
            <Image alt="pet" src={pet.imageUrl} errorIcon={<LogoSVG />} imageStyle={{objectFit: "cover"}} style={{width: "100%"}} />
        ) : (
            <LogoSVG />
        );
    };

    return (
        <Container maxWidth="lg">
            <Grid container spacing={1} className={classes.wrapper}>
                {step === "availability" && (
                    <>
                        <Grid item xs={12} className={classes.section}>
                            <Typography variant="h3" className={classes.sectionTitle}>
                                What type of pet do you have?
                            </Typography>
                            <FormControl variant="outlined" className={classes.selectWidth} required>
                                <InputLabel id="specialty-filter-label">Type of pet</InputLabel>
                                <Select
                                    labelId="specialty-filter-label"
                                    value={getBasicPetTypeValue(selectedPetType, isOtherTypeSelected)}
                                    onChange={(e) => handleBasicPetTypeChange(e.target.value as string)}
                                    label="Type of pet"
                                    displayEmpty
                                >
                                    <MenuItem value="CANINE">Dog</MenuItem>
                                    <MenuItem value="FELINE">Cat</MenuItem>
                                    <MenuItem value={OTHER_PET_TYPE_VALUE}>Other</MenuItem>
                                </Select>
                            </FormControl>
                            {isOtherTypeSelected &&
                                <FormControl variant="outlined" className={classes.selectWidth} required style={{marginLeft: 12}}>
                                    <InputLabel id="other-pet-type-label">Other type</InputLabel>
                                    <Select
                                        labelId="other-pet-type-label"
                                        value={isBasicPetType(selectedPetType) ? "" : selectedPetType}
                                        onChange={(e) => handleOtherPetTypeChange(e.target.value as string)}
                                        label="Other type"
                                        displayEmpty
                                    >
                                        {otherSpecialityOptions.map((s) => (
                                            <MenuItem key={s.value} value={s.value}>
                                                {s.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            }
                        </Grid>
                        <Grid item xs={12} className={classes.section}>
                            <Typography variant="h3" className={classes.sectionTitle}>
                                What state are you located in?
                            </Typography>
                            <FormControl variant="outlined" className={classes.selectWidth} required>
                                <InputLabel id="state-filter-label">State</InputLabel>
                                <Select
                                    labelId="state-filter-label"
                                    value={selectedState}
                                    onChange={(e) => handleStateChange(e.target.value as string)}
                                    label="State"
                                    displayEmpty
                                >
                                    {states.map((s) => (
                                        <MenuItem key={s.value} value={s.value}>
                                            {s.value}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} className={classes.section}>
                            <Typography variant="h3" className={classes.sectionTitle}>
                                When would you like your virtual visit?
                            </Typography>
                            <Calendar
                                value={selectedDate || new Date()}
                                handleChange={(day) => {
                                    if (!selectedPetType) {
                                        enqueueSnackbar("Please select a pet type", {variant: "error"});
                                        return;
                                    }
                                    fetchVets(day, selectedPetType, specialtyFilter || selectedPetType);
                                }}
                                alignLeft
                                loading={showSpinner}
                            />
                        </Grid>

                        <Grid item xs={12} className={classes.section}>
                            <Typography variant="h3" className={classes.sectionTitle}>
                                Choose a time that works best for you
                            </Typography>
                            {selectedPetType && (
                                <Typography variant="body2" color="textSecondary" style={{marginBottom: 16}}>
                                    {moment(selectedDate).format("dddd, MMMM D, YYYY")}
                                </Typography>
                            )}
                            {!selectedPetType && (
                                <Typography color="textSecondary">
                                    Select your pet type to view available appointment times.
                                </Typography>
                            )}
                            {showSpinner && (
                                <Box className={classes.leftAligned}>
                                    <CircularProgress />
                                </Box>
                            )}
                            {selectedPetType && (!vetProfiles.length || !availableTimeSlots.length) && !showSpinner && (
                                <Box className={classes.leftAligned}>
                                    <Typography style={{marginBottom: 16}}>
                                        We don&apos;t have any openings on {moment(selectedDate).format("dddd, MMMM D, YYYY")} right now. Take a look at our other available dates and find a time that works for you and {selectedPetName}.
                                    </Typography>
                                </Box>
                            )}
                            {selectedPetType && vetProfiles.length > 0 && availableTimeSlots.length > 0 && !showSpinner &&
                                vetProfiles.map((profile) => {
                                    const schedulingForUserTimezone = getSchedulingForUserTimezone(profile.schedulingDays);
                                    const timeslots = getTimeslotsForSelectedDate(schedulingForUserTimezone, selectedDate);
                                    return (
                                        <AvaliableVetListItem
                                            name={`Dr. ${profile.lastName || ""}`}
                                            key={profile.id}
                                            profileId={profile.id}
                                            image={profile.image}
                                            userId={profile.userId}
                                            profile={profile}
                                            speciality={profile.speciality}
                                            scheduling={timeslots}
                                            onTimeSlotSelect={(timeslot) =>
                                                handleTimeSlotSelect(profile, profile.id, profile.userId, timeslot)
                                            }
                                        />
                                    );
                                })}
                        </Grid>
                    </>
                )}

                {step === "pet_select" && (
                    <>
                        <Grid item xs={12} className={classes.section}>
                            <Typography variant="h3" className={classes.sectionTitle}>Select your pet</Typography>
                        </Grid>
                        {petsList.length === 0 ? (
                            <Grid item xs={12}>
                                <Typography style={{marginBottom: 16}}>
                                    You don&apos;t have a pet of this type on your account. Please add one to continue.
                                </Typography>
                                <Button type="button" variant="contained" color="primary" onClick={handleAddPet}>
                                    Add a pet
                                </Button>
                            </Grid>
                        ) : (
                            <>
                                <Grid container spacing={2} justify="flex-start">
                                    {petsList.map((pet) => (
                                        <Grid item xs={6} sm={2} key={pet.id} container justify="flex-start">
                                            <Box onClick={() => setSelectedPetId(pet.id)} textAlign="center">
                                                <Avatar
                                                    variant="square"
                                                    className={`${classes.avatar} ${
                                                        selectedPetId === pet.id ? classes.selectedPet : ""
                                                    }`}
                                                >
                                                    {renderPetAvatar(pet)}
                                                </Avatar>
                                                <Typography>{pet.name}</Typography>
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>
                                <Box marginTop={4} display="flex" flexDirection="column" style={{gap: 12}}>
                                    <Button variant="contained" color="primary" onClick={handlePetContinue}>
                                        Continue
                                    </Button>
                                    <Button variant="outlined" color="primary" onClick={handleAddPet}>
                                        Add another pet
                                    </Button>
                                </Box>
                            </>
                        )}
                    </>
                )}

                <Modal
                    aria-labelledby="guest-auth-modal"
                    className={classes.modal}
                    open={showAuthModal}
                    onClose={() => setShowAuthModal(false)}
                    closeAfterTransition
                    BackdropComponent={Backdrop}
                    BackdropProps={{timeout: 500}}
                >
                    <Fade in={showAuthModal}>
                        <GuestAuthModal classes={classes} onLogin={handleLogin} onSignUp={handleSignUp} />
                    </Fade>
                </Modal>

                <Modal
                    className={classes.modal}
                    open={showContactUs}
                    onClose={() => setShowContactUs(false)}
                    closeAfterTransition
                    BackdropComponent={Backdrop}
                    BackdropProps={{timeout: 500}}
                >
                    <Fade in={showContactUs}>
                        <GuestContactModal classes={classes} onClose={() => setShowContactUs(false)} />
                    </Fade>
                </Modal>
            </Grid>
        </Container>
    );
};

const GuestAuthModal = ({classes, onLogin, onSignUp}) => (
    <div className={classes.paper}>
        <Typography variant="h2" gutterBottom>
            Sign in to continue
        </Typography>
        <Typography variant="body2" gutterBottom>
            Create an account or log in to book your appointment.
        </Typography>
        <Button type="button" variant="contained" color="primary" className={classes.button} onClick={onLogin}>
            Log in
        </Button>
        <Button type="button" variant="outlined" color="primary" className={classes.button} onClick={onSignUp}>
            Sign up
        </Button>
    </div>
);

const GuestContactModal = ({classes, onClose}) => (
    <div className={classes.paper}>
        <Typography variant="h2" gutterBottom>
            Contact Us
        </Typography>
        <Typography variant="body2" gutterBottom>
            Hours: <span style={{fontWeight: 400}}>9am - midnight EST</span>
        </Typography>
        <Typography variant="body2" gutterBottom>
            Email:{" "}
            <a href="mailto:hello@thevideovets.com" style={{fontWeight: 400}}>
                hello@thevideovets.com
            </a>
        </Typography>
        <Button type="button" variant="outlined" color="primary" className={classes.button} onClick={onClose}>
            Close
        </Button>
    </div>
);

export default GuestAvailableVet;
